import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Chip } from "../Chip/index.js";
import { Popper, PopperContent, PopperTrigger } from "../Popper/index.js";
const MAX_CHIPS_ROWS = 1;
const EXTRA_WIDTH_FROM_CLOSE = 28;
function ChipGroup({ items, selected, onDelete, onClearAll, maxChipsRows = MAX_CHIPS_ROWS, extraWidthFromClose = EXTRA_WIDTH_FROM_CLOSE, clearButtonLabel, chipVariant = 'close' }) {
    const chipsContainerRef = useRef(null);
    const overflowChipRef = useRef(null);
    const virtualChipRef = useRef(null);
    const { visibleChips } = useCalculateChipOverflow({
        chipsContainerRef,
        overflowChipRef,
        virtualChipRef,
        selectedOptions: selected,
        items,
        maxChipsRows,
        extraWidthFromClose
    });
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-space-y-1.2",
        children: [
            /*#__PURE__*/ jsx(Chip, {
                ref: virtualChipRef,
                className: "tw-absolute -tw-left-full -tw-z-7 tw-opacity-0"
            }),
            /*#__PURE__*/ jsxs("div", {
                ref: chipsContainerRef,
                className: clsx('tw-pt-0.8 tw-flex tw-items-center tw-gap-0.8 tw-flex-wrap', 0 === selected.length && 'tw-hidden'),
                children: [
                    selected.map((value, index)=>index >= visibleChips ? null : /*#__PURE__*/ jsx(Chip, {
                            variant: chipVariant,
                            onClose: ()=>onDelete?.(value),
                            children: items.find((el)=>el.value === value)?.label
                        }, value)),
                    /*#__PURE__*/ jsxs(Popper, {
                        children: [
                            /*#__PURE__*/ jsx(PopperTrigger, {
                                children: /*#__PURE__*/ jsxs("div", {
                                    ref: overflowChipRef,
                                    className: "tw-border tw-border-solid tw-rounded-2 tw-py-0.8 tw-px-1.2 tw-bg-primary-light-400 tw-text-neutral-light-100 tw-font-body5",
                                    hidden: visibleChips >= selected.length,
                                    children: [
                                        "+",
                                        selected.length - visibleChips
                                    ]
                                })
                            }),
                            /*#__PURE__*/ jsx(PopperContent, {
                                removePadding: true,
                                removePortal: true,
                                align: "end",
                                children: /*#__PURE__*/ jsx("ul", {
                                    className: "tw-flex tw-flex-col tw-max-h-28 tw-overflow-auto",
                                    children: selected.map((value, index)=>index < visibleChips ? null : /*#__PURE__*/ jsx("li", {
                                            className: "tw-px-2.4 tw-py-1.2",
                                            children: /*#__PURE__*/ jsx(Chip, {
                                                variant: chipVariant,
                                                onClose: ()=>onDelete?.(value),
                                                children: items.find((el)=>el.value === value)?.label
                                            })
                                        }, value))
                                })
                            })
                        ]
                    })
                ]
            }),
            selected.length > 0 && clearButtonLabel && /*#__PURE__*/ jsx("button", {
                type: "button",
                className: "tw-text-primary-light-500 tw-font-link1",
                onClick: onClearAll,
                children: clearButtonLabel
            })
        ]
    });
}
function useCalculateChipOverflow({ chipsContainerRef, overflowChipRef, virtualChipRef, selectedOptions, items, maxChipsRows, extraWidthFromClose }) {
    const [visibleChipsState, setVisibleChips] = useState(0);
    const effectKey = selectedOptions.length;
    useEffect(()=>{
        const container = chipsContainerRef.current;
        const virtualChip = virtualChipRef.current;
        if (!container || !container.firstChild || !virtualChip) return void setVisibleChips(0);
        const flexGap = 8;
        const overflowChipWidth = overflowChipRef.current?.getBoundingClientRect().width ?? 0;
        let lines = 1;
        let visibleChips = 0;
        let totalWidth = 0;
        selectedOptions.forEach((option)=>{
            const chip = virtualChip;
            chip.innerHTML = items.find((el)=>el.value === option)?.label ?? '';
            const chipWidth = chip.getBoundingClientRect().width + extraWidthFromClose;
            if (totalWidth + chipWidth + (lines >= maxChipsRows ? overflowChipWidth + flexGap : 0) > container.offsetWidth) {
                lines += 1;
                totalWidth = 0;
            }
            if (lines <= maxChipsRows) {
                totalWidth += chipWidth + flexGap;
                visibleChips += 1;
            }
        });
        setVisibleChips(visibleChips);
    }, [
        effectKey
    ]);
    return {
        visibleChips: visibleChipsState
    };
}
export { ChipGroup };
